/**
 * Static content for the 7-step onboarding flow — ambient colors per
 * step, journey definitions, PAR-Q questions, VDOT qualitative
 * mapping, and journey-specific first-messages shown in Step 7
 * Welcome.
 *
 * Copy sourced verbatim from
 * `.claude/design-artifacts/2026-04-21-onboarding-landing/README.md`
 * with our reconciliation decisions applied (see `Decisions &
 * Handoff.md` in the same folder):
 *   - PAR-Q: app's existing 7 questions (canonicalization is separate
 *     tech-debt; not in Phase 2 scope)
 *   - health_fitness motivation chip list: "Weight management" is
 *     intentionally absent per RED-S coach-prompt policy
 *   - VDOT qualitative bands: tightened to 28-32 / 32-38 / 38-42 /
 *     42-48 (see Claude Design review earlier this session)
 */

import { LEAD_COACH } from './coaches';

// ──────────────────────────────────────────────────────────────────────────
// Journey
// ──────────────────────────────────────────────────────────────────────────

export type JourneyId =
  | 'competitive_solo'
  | 'competitive_team'
  | 'time_goal'
  | 'health_fitness'
  | 'social';

/** Journeys that use the VDOT race-time calculator in Step 5. */
export const COMPETITIVE_JOURNEYS: JourneyId[] = [
  'competitive_solo',
  'competitive_team',
  'time_goal',
];

export type JourneyOption = {
  id: JourneyId;
  label: string;
  description: string;
};

/** 4 user-selectable journeys shown in Step 1. `competitive_team` is
 *  auto-set elsewhere (when joining a coached team) — not selectable
 *  here per current vanilla product logic. Tracked as tech-debt. */
export const JOURNEY_OPTIONS: JourneyOption[] = [
  {
    id: 'competitive_solo',
    label: 'I want to compete',
    description: 'PRs, race strategy, periodized training.',
  },
  {
    id: 'time_goal',
    label: 'I have a time goal',
    description: 'A specific time target. Your plan builds around it.',
  },
  {
    id: 'health_fitness',
    label: 'I want to get healthier',
    description: 'Build a running habit, health, wellbeing.',
  },
  {
    id: 'social',
    label: 'I run with friends',
    description: 'Team feed, kudos, group motivation.',
  },
];

// ──────────────────────────────────────────────────────────────────────────
// Per-step metadata — ambient color + label + context line
// ──────────────────────────────────────────────────────────────────────────

export type StepMeta = {
  step: number;
  label: string;
  context: string;
  /** Left-panel ambient bg on desktop, top strip on mobile. */
  ambientBg: string;
};

/**
 * O1 (2026-08-31) — AMBIENTS ARE NOW PER CHAPTER, NOT PER STEP.
 *
 * The founder-ratified chapter handoff collapses the seven step ambients onto
 * the four chapter ambients in `lib/onboardingChapters.ts`, so a chapter turn
 * is the only time the panel colour moves. Two colours retire outright: the
 * step-2 sage `#DDE8D9` (location now wears chapter 1 sand) and the step-6
 * rose `#E6DCE2` (focus now wears chapter 3 teal grey); step 4 leaves its warm
 * neutral for chapter 2's cool blue.
 *
 * `label` and `context` stay for the surfaces that still read them directly
 * (`CollectionBeat`'s metaOverride, the onboarding-lite chrome and the
 * defensive shell fallback). In the chaptered flow the panel takes both from
 * the chapter instead, so these no longer render for steps 1 to 7.
 *
 * ⛔ Keep every `ambientBg` here equal to its chapter's ambient. Pinned in
 * `lib/onboardingChapters.o1.test.ts`.
 */
export const STEP_META: StepMeta[] = [
  {
    step: 1,
    label: 'Your journey',
    context: 'This shapes everything: your plan, your coaching voice, your dashboard.',
    ambientBg: '#E4DDD5', // chapter 1 — warm sand
  },
  {
    step: 2,
    label: 'Your location',
    context: 'So your coaches know the weather you run in.',
    ambientBg: '#E4DDD5', // chapter 1 — warm sand (sage #DDE8D9 retired)
  },
  {
    step: 3,
    label: 'Health check',
    context: 'A standard screening before starting an exercise program.',
    ambientBg: '#DDE3EB', // chapter 2 — cool blue
  },
  {
    step: 4,
    label: 'Body metrics',
    context: 'Optional: unlocks weight-based nutrition and safety monitoring.',
    ambientBg: '#DDE3EB', // chapter 2 — cool blue (warm neutral #EBE3DC retired)
  },
  {
    step: 5,
    label: 'Your level',
    context: 'Honest answers help your coaches start from the right place.',
    ambientBg: '#DCE6E6', // chapter 3 — teal grey
  },
  {
    step: 6,
    label: 'Your focus',
    context: 'The final shape of your plan comes from this step.',
    ambientBg: '#DCE6E6', // chapter 3 — teal grey (rose #E6DCE2 retired)
  },
  {
    step: 7,
    label: 'All set',
    context: 'Your coaching team is ready to meet you.',
    ambientBg: '#EBE4DB', // chapter 4 — warm coffee
  },
];

// ──────────────────────────────────────────────────────────────────────────
// PAR-Q — keep the app's existing 7 questions in Phase 2
//   (canonicalization to CSEP 2002 is a separate tech-debt item)
// ──────────────────────────────────────────────────────────────────────────

export type ParqQuestion = {
  key: string;
  question: string;
};

export const PARQ_QUESTIONS: ParqQuestion[] = [
  {
    key: 'heart_condition',
    question: 'Has a doctor ever told you that you have a heart condition?',
  },
  {
    key: 'chest_pain',
    question: 'Do you feel pain in your chest when doing physical activity?',
  },
  {
    key: 'dizziness',
    question: 'Have you lost balance from dizziness or lost consciousness in the past year?',
  },
  {
    key: 'bone_joint',
    question: 'Do you have a bone or joint problem that could be made worse by running?',
  },
  {
    key: 'medication',
    question: 'Is your doctor currently prescribing medication for blood pressure or a heart condition?',
  },
  {
    key: 'medical_supervision',
    question: 'Has a doctor ever told you that you should only do medically supervised physical activity?',
  },
  {
    key: 'other_reason',
    question: 'Do you know of any other reason why you should not do physical activity?',
  },
];

// ──────────────────────────────────────────────────────────────────────────
// Step 5 — qualitative pace options (health_fitness + social)
//   Maps to VDOT midpoint for initial zone calibration; superseded by
//   actual run data after first sessions. Tightened from Claude
//   Design's initial mapping (which overshot at the upper band).
// ──────────────────────────────────────────────────────────────────────────

export type QualPaceId = 'starting' | 'comfortable' | 'regular' | 'experienced';

export type QualPaceOption = {
  id: QualPaceId;
  label: string;
  description: string;
  /** Inclusive VDOT range used for initial zone calibration. */
  vdotRange: [number, number];
};

export const QUAL_PACE_OPTIONS: QualPaceOption[] = [
  {
    id: 'starting',
    label: 'Just starting out',
    description: 'Building toward 20 to 30 minutes without stopping.',
    vdotRange: [28, 32],
  },
  {
    id: 'comfortable',
    label: 'Getting comfortable',
    description: 'Can run 20 to 30 minutes, but it\'s still work.',
    vdotRange: [32, 38],
  },
  {
    id: 'regular',
    label: 'Running regularly',
    description: 'Easy runs feel easy, looking to do more.',
    vdotRange: [38, 42],
  },
  {
    id: 'experienced',
    label: 'Been running a while',
    description: 'I have a base, I want smart guidance.',
    vdotRange: [42, 48],
  },
];

/** Compute the midpoint VDOT for a qualitative selection. */
export function qualPaceMidVdot(id: QualPaceId): number {
  const opt = QUAL_PACE_OPTIONS.find((o) => o.id === id);
  if (!opt) throw new Error(`Unknown qual pace: ${id}`);
  return (opt.vdotRange[0] + opt.vdotRange[1]) / 2;
}

// ──────────────────────────────────────────────────────────────────────────
// Step 6 — per-journey question options
// ──────────────────────────────────────────────────────────────────────────

export const DISCIPLINE_OPTIONS = [
  { id: 'road', label: 'Road' },
  { id: 'track', label: 'Track' },
  { id: 'trail', label: 'Trail & ultra' },
  { id: 'xc', label: 'Cross-Country' },
  { id: 'multi', label: 'Mixed & Exploring' },
] as const;

export const GOAL_EVENT_CHIPS = [
  '5K',
  '10K',
  'Half Marathon',
  'Marathon',
  'Ultra',
  '800m',
  '1500m/Mile',
  '3K/5K Track',
  'XC Season',
  'No specific goal',
] as const;

export type TimeGoalType = 'first_finish' | 'beat_time' | 'pr' | 'build';

export const TIME_GOAL_TYPES: { id: TimeGoalType; label: string; description: string }[] = [
  {
    id: 'first_finish',
    label: 'First finish',
    description: "I've never done this distance before.",
  },
  {
    id: 'beat_time',
    label: 'Beat a specific time',
    description: 'I have a target in my head.',
  },
  {
    id: 'pr',
    label: 'Set a new PR',
    description: "I've done it, I want to go faster.",
  },
  {
    id: 'build',
    label: 'Build to the distance',
    description: "I'm not race-ready yet.",
  },
];

export const TIME_GOAL_DISTANCE_CHIPS = ['5K', '10K', 'Half Marathon', 'Marathon', 'Other'] as const;

export type HealthFreqId = '1_2' | '3_4' | '5plus' | 'varies';

export const HEALTH_FREQ_OPTIONS: { id: HealthFreqId; label: string; description: string }[] = [
  {
    id: '1_2',
    label: 'Just getting started',
    description: '1 to 2 runs a week.',
  },
  {
    id: '3_4',
    label: 'Building a habit',
    description: '3 to 4 runs a week.',
  },
  {
    id: '5plus',
    label: 'Already consistent',
    description: '5+ runs a week.',
  },
  {
    id: 'varies',
    label: 'It varies a lot',
    description: 'Depends on the week.',
  },
];

/** "Weight management" is INTENTIONALLY absent — conflicts with RED-S
 *  coach-prompt policy (coaches are instructed never to engage with
 *  weight loss as a performance strategy). See
 *  `api/index.js:2143-2166`. Do not add it. */
export const HEALTH_MOTIVATION_CHIPS = [
  'Energy',
  'Feel stronger',
  'Stress relief',
  'Sleep',
  'Mental health',
  'General health',
  'Just enjoy it',
] as const;

export type SocialGroupId = 'club' | 'friends' | 'mix' | 'solo_looking';

export const SOCIAL_GROUP_OPTIONS: { id: SocialGroupId; label: string; description: string }[] = [
  {
    id: 'club',
    label: 'Regular running club or team',
    description: 'Scheduled group runs.',
  },
  {
    id: 'friends',
    label: 'A few close friends or a partner',
    description: 'Informal, consistent.',
  },
  {
    id: 'mix',
    label: 'Mix of people. Varies.',
    description: 'Different folks on different days.',
  },
  {
    id: 'solo_looking',
    label: 'Mostly solo. Want to find others.',
    description: 'Looking for a crew.',
  },
];

export const SOCIAL_FREQUENCY_CHIPS = [
  'Every run',
  'A few times/week',
  'Once a week',
  'Occasionally',
] as const;

// ──────────────────────────────────────────────────────────────────────────
// Step 7 — journey-specific first-message from the lead coach (AIro)
// ──────────────────────────────────────────────────────────────────────────

export const WELCOME_FIRST_MESSAGES: Record<JourneyId, string> = {
  competitive_solo:
    'Your first week is built around the aerobic base: the foundation every PR is built on. Let\'s start right.',
  // Same copy as competitive_solo — competitive_team is auto-set only
  // when joining a coached team, but we preseed both for safety.
  competitive_team:
    'Your first week is built around the aerobic base: the foundation every PR is built on. Let\'s start right.',
  // B459 (walk 2026-10-22): the old line said "I've seen your goal. The plan
  // I've built gives you enough runway, and a buffer", but no target time is
  // asked in onboarding and the plan does not exist yet at this screen. A
  // sentence claiming a goal or a plan is a reserved-promise class (only code
  // may cause it), so this one claims neither.
  time_goal:
    "Chasing a time starts with a steady base. Let's build the foundation first and take it from there.",
  health_fitness:
    'No pressure, just progress. Your first week is short, intentional, and designed to make next week feel easy.',
  social:
    "Running with others is the best accountability system there is. Let's make sure your plan fits your crew's rhythm.",
};

/** Speaker of the welcome first-message. */
export const WELCOME_SPEAKER = LEAD_COACH;
